<script setup>
import Avatar from 'next/avatar/Avatar.vue';
import { ref, computed, watch, nextTick } from 'vue';
import { useKeyboardNavigableList } from 'dashboard/composables/useKeyboardNavigableList';

// Menu di autocompletamento per menzionare i membri di un gruppo Telegram (stile Telegram).
// La lista arriva gia' pronta dal composer: qui filtriamo per testo digitato dopo la '@'.
const props = defineProps({
  participants: {
    type: Array,
    default: () => [],
  },
  searchKey: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['select']);

const listRef = ref(null);
const selectedIndex = ref(0);

const items = computed(() => {
  const search = props.searchKey?.trim().toLowerCase() || '';
  return (
    props.participants
      // Se stesso non ha senso menzionarlo, e un account senza id non e' raggiungibile.
      .filter(
        participant => participant && participant.id && !participant.is_self
      )
      .filter(participant => {
        if (!search) return true;
        const name = (participant.name || '').toLowerCase();
        const username = (participant.username || '').toLowerCase();
        return name.includes(search) || username.includes(search);
      })
      .slice(0, 30)
  );
});

const adjustScroll = () => {
  nextTick(() => {
    if (!listRef.value) return;
    const el = listRef.value.querySelector(
      `#telegram-mention-item-${selectedIndex.value}`
    );
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'auto' });
  });
};

const onSelect = () => {
  const participant = items.value[selectedIndex.value];
  if (participant) emit('select', participant);
};

useKeyboardNavigableList({
  items,
  onSelect,
  adjustScroll,
  selectedIndex,
});

watch(items, newItems => {
  if (newItems.length < selectedIndex.value + 1) selectedIndex.value = 0;
});

const onHover = index => {
  selectedIndex.value = index;
};

const onClick = index => {
  selectedIndex.value = index;
  onSelect();
};
</script>

<template>
  <div>
    <ul
      v-if="items.length"
      ref="listRef"
      class="vertical dropdown menu mention--box bg-n-solid-1 p-1 rounded-xl text-sm overflow-auto absolute w-full z-20 shadow-md left-0 leading-[1.2] bottom-full max-h-[12.5rem] border border-solid border-n-strong"
      role="listbox"
    >
      <li
        v-for="(participant, index) in items"
        :id="`telegram-mention-item-${index}`"
        :key="participant.id"
      >
        <div
          :class="{ 'bg-n-alpha-black2': index === selectedIndex }"
          class="flex items-center px-2 py-1 rounded-md cursor-pointer"
          role="option"
          @click="onClick(index)"
          @mouseover="onHover(index)"
        >
          <div class="ltr:mr-2 rtl:ml-2">
            <Avatar :name="participant.name" rounded-full />
          </div>
          <div
            class="overflow-hidden flex-1 max-w-full whitespace-nowrap text-ellipsis"
          >
            <h5
              class="overflow-hidden mb-0 text-sm whitespace-nowrap text-n-slate-11 text-ellipsis"
              :class="{ 'text-n-slate-12': index === selectedIndex }"
            >
              {{ participant.name }}
            </h5>
            <div
              class="overflow-hidden text-xs whitespace-nowrap text-ellipsis text-n-slate-10"
              :class="{ 'text-n-slate-11': index === selectedIndex }"
            >
              {{
                participant.username
                  ? `@${participant.username}`
                  : 'menzione per nome'
              }}
            </div>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>
